import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Section } from '@vnetwork/ui/layout';
import {
  Button,
  Input,
  Textarea,
  SegmentedControl,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { useWaapPurge, type WaapPurgeRequest } from '@/api/waap';
import { errMessage } from '@/pages/services/waap/waap-util';
import styles from './PurgeTab.module.css';

const MODES = [
  { value: 'url', label: 'Purge By URL' },
  { value: 'custom', label: 'Custom Purge' },
  { value: 'everything', label: 'Purge Everything' },
];

const OPERATORS = [
  { value: 'startswith', label: 'Prefix' },
  { value: 'endswith', label: 'Suffix' },
];

const MODE_HINT: Record<string, string> = {
  url: 'Purge assets in the CDN cache that fully match the given URL(s) — one per line.',
  custom: 'Purge assets with a designated URL prefix (e.g. /foo) or suffix (e.g. .mp4).',
  everything: 'Purge all cached files. This may slow your website temporarily.',
};

/**
 * Purge — website-detail tab. Three cache-purge modes (exact URLs / prefix-suffix
 * custom / everything) → POST /waf/eagle/purge. Website fixed by the detail route.
 */
export function PurgeTab() {
  const { domainId } = useParams<{ domainId: string }>();
  const { show } = useToast();

  const [mode, setMode] = useState<WaapPurgeRequest['mode']>('url');
  const [urls, setUrls] = useState('');
  const [operator, setOperator] = useState('startswith');
  const [pattern, setPattern] = useState('');

  const purge = useWaapPurge(domainId ?? '', {
    onSuccess: () => show({ tone: 'success', title: 'Purge requested' }),
    onError: (e) => show({ tone: 'danger', title: 'Purge failed', description: errMessage(e) }),
  });

  const urlList = urls.split('\n').map((s) => s.trim()).filter(Boolean);
  const valid = mode === 'everything' || (mode === 'url' ? urlList.length > 0 : pattern.trim().length > 0);

  const submit = () => {
    const body: WaapPurgeRequest =
      mode === 'url'
        ? { mode, urls: urlList }
        : mode === 'custom'
          ? { mode, operator, pattern: pattern.trim() }
          : { mode };
    purge.mutate(body);
  };

  return (
    <Section
      title="Purge Cache"
      description="Purge cached files and fetch the latest version from the web server."
    >
      <Card>
        <CardHeader>
          <CardTitle>Purge</CardTitle>
          <CardDescription>{MODE_HINT[mode]}</CardDescription>
        </CardHeader>
        <CardBody className={styles.bodyGrid}>
          <SegmentedControl items={MODES} value={mode} onValueChange={(v) => setMode(v as WaapPurgeRequest['mode'])} aria-label="Purge mode" />

          {mode === 'url' && (
            <Field label="URLs" description="One full URL per line.">
              {(p) => (
                <Textarea
                  {...p}
                  rows={4}
                  placeholder={'https://mywebsite.com/folder/file.txt'}
                  value={urls}
                  onChange={(e) => setUrls(e.target.value)}
                />
              )}
            </Field>
          )}

          {mode === 'custom' && (
            <div className={styles.customRow}>
              <Field label="Match" description="Prefix: purge all cached assets whose URL starts with the pattern. Suffix: purge all whose URL ends with the pattern.">
                {() => (
                  <Select value={operator} onValueChange={setOperator}>
                    <SelectTrigger className={styles.opSelect}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {OPERATORS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Input className={styles.grow} placeholder="/api/ or .mp4" value={pattern} onChange={(e) => setPattern(e.target.value)} />
            </div>
          )}

          <div className={styles.footer}>
            <Button variant={mode === 'everything' ? 'danger' : 'primary'} disabled={!valid || purge.isPending} onClick={submit}>
              {purge.isPending ? 'Purging…' : mode === 'everything' ? 'Purge Everything' : 'Purge'}
            </Button>
          </div>
        </CardBody>
      </Card>
    </Section>
  );
}
